import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { OfflineContext } from '../contexts/OfflineContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { SettingsContext, useSettings } from '../contexts/SettingsContext';
import { LayoutDashboard, ShoppingCart, Package, Tags, Users, BarChart3, Settings, LogOut, Store, WifiOff, Bell, Layers, Moon, Sun, Lock, Unlock, ArrowRightLeft, BrainCircuit, Sparkles, ClipboardCheck, Truck, ShoppingBag, RefreshCw, X, Gift, Clock as ClockIcon } from 'lucide-react';
import { ShiftContext } from '../contexts/ShiftContext';
import RegisterShift, { CloseShiftModal } from './RegisterShift';
import Clock from './Clock';
import CommandPalette from './CommandPalette';

export default function Layout() {
  const { user, logout } = useContext(AuthContext);
  const { online, pendingCount, syncing } = useContext(OfflineContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSession, loading } = useContext(ShiftContext);
  const { settings: globalSettings } = useSettings();
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    // Close sidebar on route change on mobile
    setIsSidebarOpen(false);
  }, [location.pathname]);



  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/products/low-stock').then(res => setLowStockCount(res.length)).catch(console.error);
    }
  }, [user, location.pathname]);

  const navItems = [
    { path: '/app', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard' },
    { path: '/app/attendance', label: 'Attendance', icon: ClockIcon, permission: 'dashboard' },
    { path: '/app/pos', label: 'Sales Terminal', icon: ShoppingCart, permission: 'pos' },
    { path: '/app/products', label: 'Products', icon: Package, permission: 'products' },
    { path: '/app/categories', label: 'Categories', icon: Tags, permission: 'products' },
    { path: '/app/inventory', label: 'Inventory', icon: Layers, permission: 'inventory' },
    { path: '/app/suppliers', label: 'Vendors', icon: Truck, permission: 'vendors' },
    { path: '/app/purchases', label: 'Purchases', icon: ShoppingBag, permission: 'purchases' },
    { path: '/app/transfers', label: 'Stock Transfer', icon: ArrowRightLeft, permission: 'transfers' },
    { path: '/app/inventory-insights', label: 'AI Insights', icon: BrainCircuit, permission: 'inventory' },
    { path: '/app/inventory-audit', label: 'Stock Take', icon: ClipboardCheck, permission: 'inventory' },
    { path: '/app/promotions', label: 'Marketing', icon: Sparkles, permission: 'marketing' },
    { path: '/app/gift-cards', label: 'Gift Cards', icon: Gift, permission: 'marketing' },
    { path: '/app/customers', label: 'Customers', icon: Users, permission: 'customers' },
    { path: '/app/reports', label: 'Reports', icon: BarChart3, permission: 'reports' },
    { path: '/app/sales-history', label: 'Sales Ledger', icon: ShoppingBag, permission: 'reports' },
    { path: '/app/audit-trail', label: 'Audit Trail', icon: ClipboardCheck, permission: 'admin_access' },
    { path: '/app/admin', label: 'Settings', icon: Settings, permission: 'settings' }
  ].filter(item => {
    if (user?.role === 'admin') return true;
    if (user?.permissions?.includes(item.permission)) return true;
    // Default visibility for legacy users or basic access
    if (!user?.permissions) {
      return item.path === '/app' || item.path === '/app/pos' || item.path === '/app/customers';
    }
    return false;
  });

  return (
    <div className="flex h-screen overflow-hidden transition-colors duration-300 print:h-auto print:overflow-visible" style={{ backgroundColor: 'var(--bg-main)' }}>
      {/* Mobile Backdrop */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-30 md:hidden animate-fade-in"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`print:hidden fixed md:relative w-64 h-full flex flex-col transition-all duration-300 shadow-2xl z-40 border-r transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`} 
        style={{ backgroundColor: 'var(--sidebar)', borderColor: 'var(--border)' }}
      >
        <div className="p-6 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            {globalSettings.logo_display_mode !== 'title' && (
              globalSettings.store_logo ? (
                <img src={globalSettings.store_logo} alt="Logo" className="w-8 h-8 rounded-lg object-contain bg-white shrink-0" />
              ) : (
                <div className="p-2 rounded-xl text-white shadow-lg shrink-0" style={{ backgroundColor: 'var(--brand)' }}>
                  <Store size={24} />
                </div>
              )
            )}
            {globalSettings.logo_display_mode !== 'logo' && (
              <h1 className="text-xl font-bold tracking-wide truncate" style={{ color: theme === 'nexus' ? '#fff' : '#1e293b' }}>
                {globalSettings.store_name ? (
                  globalSettings.store_name
                ) : (
                  <>Nexus<span style={{ color: 'var(--brand)' }}>POS</span></>
                )}
              </h1>
            )}
          </Link>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-theme-muted hover:text-brand transition-colors p-1">
            <X size={24} />
          </button>
        </div>
        
        <nav className="flex-1 px-4 space-y-1 mt-4 overflow-y-auto custom-scrollbar">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-4 px-2" style={{ color: 'var(--text-muted)' }}>Menu</div>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path !== '/app' && location.pathname.startsWith(item.path));
            return (
              <Link 
                key={item.path} 
                to={item.path} 
                className={`flex items-center gap-3 py-3 px-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? 'font-bold' 
                    : 'hover:bg-brand/5'
                }`}
                style={{ 
                  backgroundColor: isActive ? 'var(--sidebar-active)' : 'transparent',
                  color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)'
                }}
              >
                <Icon size={20} style={{ color: isActive ? 'var(--sidebar-active-text)' : 'var(--sidebar-text)' }} />
                <span className="flex-1">{item.label}</span>
                {item.path === '/app/inventory' && lowStockCount > 0 && (
                  <span className="bg-rose-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full animate-pulse">
                    {lowStockCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 space-y-2 border-t" style={{ borderColor: 'var(--border)' }}>
          <button 
            onClick={toggleTheme}
            className="flex items-center gap-3 w-full py-3 px-3 rounded-xl transition-all font-medium"
            style={{ color: 'var(--sidebar-text)', backgroundColor: 'transparent' }}
          >
            {theme === 'nexus' ? <Sun size={20} /> : <Moon size={20} />}
            {theme === 'nexus' ? 'Light Mode' : 'Midnight Mode'}
          </button>
          
          <button 
            onClick={() => { logout(); navigate('/login'); }} 
            className="flex items-center gap-3 w-full py-3 px-3 rounded-xl hover:bg-rose-500/10 hover:text-rose-500 transition-all font-medium"
            style={{ color: 'var(--text-muted)' }}
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden print:h-auto print:overflow-visible">
        {/* Top Header */}
        <header className="print:hidden h-16 border-b flex items-center justify-between px-4 md:px-8 z-10" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden p-2 rounded-xl text-theme-muted hover:bg-brand/5 hover:text-brand transition-all"
            >
              <Layers size={24} />
            </button>
            <div className="flex items-center gap-2">
              {!online && (
                <div className="flex items-center gap-2 text-rose-600 bg-rose-50 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium animate-pulse border border-rose-200">
                  <WifiOff size={16} /> <span className="hidden sm:inline">Offline Mode</span>
                </div>
              )}
              {(pendingCount > 0 || syncing) && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border border-amber-200">
                  <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> 
                  <span className="hidden sm:inline">{syncing ? 'Syncing...' : `${pendingCount} Pending`}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <Clock />
            <button style={{ color: 'var(--text-muted)' }} className="hover:text-brand transition-colors relative">
              <Bell size={20} />
              <span className="absolute top-0 right-0 w-2 h-2 bg-rose-500 rounded-full"></span>
            </button>
            
            {activeSession && (
              <button 
                onClick={() => setShowCloseModal(true)}
                className="flex items-center gap-2 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white px-4 py-2 rounded-xl transition-all font-black text-[10px] uppercase tracking-widest border border-rose-500/20"
              >
                <Lock size={14} /> End Shift
              </button>
            )}
            <div className="flex items-center gap-3 border-l pl-6" style={{ borderColor: 'var(--border)' }}>
              <div className="text-right">
                <p className="text-sm font-bold leading-tight capitalize" style={{ color: 'var(--text-main)' }}>{user?.name || 'Admin User'}</p>
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--brand)' }}>{user?.role || 'Role'}</p>
              </div>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold shadow-soft" style={{ backgroundColor: 'var(--brand)', color: '#fff' }}>
                {(user?.name || 'A')[0].toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-8 bg-theme-main print:p-0 print:bg-transparent print:overflow-visible">
          <div className="max-w-7xl mx-auto animate-fade-in-up">
            <Outlet />
          </div>
        </main>
      </div>
      {/* Overlays */}
      <CommandPalette isOpen={isPaletteOpen} onClose={() => setIsPaletteOpen(false)} />
      {!loading && <RegisterShift />}
      {showCloseModal && <CloseShiftModal onClose={() => setShowCloseModal(false)} />}
    </div>
  );
}